import { useState, type FormEvent } from "react";
import { apiRequest, jsonBody, setCsrfToken } from "../api/client";
import type { SessionUser } from "../App";

interface AuthResponse {
  ok: true;
  user: SessionUser;
  csrfToken: string;
}

export function LoginPage() {
  const [bootstrap, setBootstrap] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      const response = await apiRequest<AuthResponse>(bootstrap ? "/api/auth/bootstrap" : "/api/auth/login", {
        method: "POST",
        body: jsonBody(values)
      });
      setCsrfToken(response.csrfToken);
      const requestedDestination = new URLSearchParams(window.location.search).get("next") || "";
      const destination = requestedDestination.startsWith("/") && !requestedDestination.startsWith("//") ? requestedDestination : "/dashboard";
      window.location.assign(destination);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-shell">
      <section className="auth-story" aria-label="InvitedStudio platform introduction">
        <a className="platform-brand light" href="/"><span className="brand-mark">IS</span><span>invitedStudio</span></a>
        <div>
          <p className="platform-kicker">Wedding workspace</p>
          <h1>Every invitation,<br />beautifully managed.</h1>
          <p>One secure workspace for customer projects, live previews, publishing, RSVP, wishes, and media.</p>
        </div>
        <small>Private management platform · Not a sales website</small>
      </section>
      <section className="auth-panel">
        <form className="auth-card" onSubmit={submit}>
          <p className="platform-kicker">{bootstrap ? "First-time setup" : "Welcome back"}</p>
          <h2>{bootstrap ? "Create platform owner" : "Sign in to your workspace"}</h2>
          {bootstrap ? (
            <>
              <label><span>Bootstrap token</span><input name="token" type="password" autoComplete="off" minLength={20} required /></label>
              <label><span>Display name</span><input name="displayName" autoComplete="name" required /></label>
            </>
          ) : null}
          <label><span>Email address</span><input name="email" type="email" autoComplete="email" required /></label>
          <label><span>Password</span><input name="password" type="password" autoComplete={bootstrap ? "new-password" : "current-password"} minLength={bootstrap ? 12 : 1} required /></label>
          {error ? <p className="alert error" role="alert">{error}</p> : null}
          <button className="platform-button primary wide" type="submit" disabled={busy}>{busy ? "Please wait…" : bootstrap ? "Create owner" : "Sign in"}</button>
          <button className="text-button" type="button" onClick={() => { setBootstrap((value) => !value); setError(""); }}>
            {bootstrap ? "Already configured? Sign in" : "Setting up a new database? Create the first owner"}
          </button>
        </form>
      </section>
    </main>
  );
}
